// -----------------------------------------------------------
// FloraIQ - Hooks Reutilizáveis
// -----------------------------------------------------------

import { useState, useCallback, useMemo, useEffect } from "react";
import { api, editorApi } from "@/lib/api";
import {
  mapAreaPilotoFromApi,
  mapCamadaGeoFromApi,
  mapDashboardSummary,
  mapDocumentFromApi,
  mapInspectionFromApi,
  mapInspectionQuadraLinkFromApi,
  mapPartyFromApi,
  mapProjectFromApi,
  mapQualitySummaryFromApi,
  mapQuadraFromApi,
  mapRascunhoFeicaoFromApi,
  mapSpeciesFromApi,
  mapTreeFromApi,
  mapTreeMetrics3DFromApi,
  mapValidationIssueFromApi,
  mapDetectedObjectFromApi,
  mapFieldValidationNoteFromApi,
  mapTerritorialIndicatorsSummaryFromApi,
  mapTerritorialInspectionDetailFromApi,
  mapTerritorialInspectionSummaryFromApi,
  type TreeMetric3DRow,
} from "@/lib/mappers";
import type { RascunhoFeicao } from "@/types/editor-territorial";
import { dashboardStats } from "@/mocks/dashboard";
import { speciesList } from "@/mocks/configuracoes";
import { projetos } from "@/mocks/projetos";
import { individuos } from "@/mocks/cta";
import { vistorias } from "@/mocks/vistorias";
import { allInconsistencies, projectQualityRanking } from "@/mocks/quality";
import { treeMetrics3D } from "@/mocks/pointcloud";
import { documents } from "@/mocks/documentos";
import { parties } from "@/mocks/partes";

/** Hook de filtros genérico */
export function useFilters<T extends Record<string, string>>(defaults: T) {
  const [filters, setFilters] = useState<T>(defaults);

  const setFilter = useCallback((key: keyof T, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaults);
  }, [defaults]);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([k, v]) => v !== "" && v !== defaults[k]);
  }, [filters, defaults]);

  return { filters, setFilter, clearFilters, hasActiveFilters };
}

/** Hook de paginação mockada */
export function usePagination<T>(items: T[], pageSize = 10) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paginatedItems = items.slice((page - 1) * pageSize, page * pageSize);

  const goToPage = useCallback((p: number) => {
    setPage(Math.max(1, Math.min(p, totalPages)));
  }, [totalPages]);

  const nextPage = useCallback(() => goToPage(page + 1), [page, goToPage]);
  const prevPage = useCallback(() => goToPage(page - 1), [page, goToPage]);

  return { page, totalPages, paginatedItems, goToPage, nextPage, prevPage, total: items.length };
}

/** Hook de estado de carregamento (simula async) */
export function useAsyncData<T>(fetcher: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  return { data, loading, error, reload: load };
}

/** Hook de busca mockada */
export function useMockSearch<T>(items: T[], searchFields: (keyof T)[]) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((item) =>
      searchFields.some((field) => {
        const val = item[field];
        return typeof val === "string" && val.toLowerCase().includes(q);
      })
    );
  }, [items, query, searchFields]);

  return { query, setQuery, filtered };
}

/**
 * Estatísticas do dashboard: parte de dashboardStats (mock) e substitui os
 * campos ja expostos pela API real (/api/v1/dashboard/summary) quando ela
 * responde. Se o backend falhar, mantém o mock como fallback (fica em
 * source: "mock").
 */
export function useDashboardStats() {
  const [stats, setStats] = useState(dashboardStats);
  const [source, setSource] = useState<"api" | "mock">("mock");

  useEffect(() => {
    let cancelled = false;

    api
      .dashboardSummary()
      .then((response) => {
        if (cancelled) return;
        const summary = mapDashboardSummary(response);
        setStats((prev) => ({
          ...prev,
          totalArvores: summary.totalTrees,
          especiesIdentificadas: summary.totalSpecies,
          areasVerdes: summary.totalEnvironmentalUnits,
        }));
        setSource("api");
      })
      .catch(() => {
        if (!cancelled) setSource("mock");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { stats, source };
}

/**
 * Busca uma lista real via API; enquanto ela não tiver dados (banco ainda
 * sem seed) ou a chamada falhar, mantém a lista mockada. Assim que a API
 * responder com pelo menos um item, a tela passa a usar dado real.
 */
function useListWithMockFallback<T>(fetcher: () => Promise<T[]>, mockData: T[]) {
  const [items, setItems] = useState<T[]>(mockData);
  const [source, setSource] = useState<"api" | "mock">("mock");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    fetcher()
      .then((result) => {
        if (cancelled) return;
        if (result.length > 0) {
          setItems(result);
          setSource("api");
        }
      })
      .catch(() => {
        // mantém mock em caso de falha
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, source, loading };
}

export function useSpeciesList() {
  const fetcher = useCallback(() => api.species().then((rows) => rows.map(mapSpeciesFromApi)), []);
  return useListWithMockFallback(fetcher, speciesList);
}

export function useProjectsList() {
  const fetcher = useCallback(() => api.projects().then((rows) => rows.map(mapProjectFromApi)), []);
  return useListWithMockFallback(fetcher, projetos);
}

export function useTreesList() {
  const fetcher = useCallback(() => api.trees().then((rows) => rows.map(mapTreeFromApi)), []);
  return useListWithMockFallback(fetcher, individuos);
}

export function useInspectionsList() {
  const fetcher = useCallback(() => api.inspections().then((rows) => rows.map(mapInspectionFromApi)), []);
  return useListWithMockFallback(fetcher, vistorias);
}

export function useDocumentsList() {
  const fetcher = useCallback(() => api.documents().then((rows) => rows.map(mapDocumentFromApi)), []);
  return useListWithMockFallback(fetcher, documents);
}

export function usePartiesList() {
  const fetcher = useCallback(() => api.parties().then((rows) => rows.map(mapPartyFromApi)), []);
  return useListWithMockFallback(fetcher, parties);
}

export function useQualityIssuesList() {
  const fetcher = useCallback(() => api.qualityIssues().then((rows) => rows.map(mapValidationIssueFromApi)), []);
  return useListWithMockFallback(fetcher, allInconsistencies);
}

export function useQualityRankingList() {
  const fetcher = useCallback(() => api.qualitySummary().then((rows) => rows.map(mapQualitySummaryFromApi)), []);
  return useListWithMockFallback(fetcher, projectQualityRanking);
}

export function useTreeMetrics3DList() {
  const fetcher = useCallback(
    () => api.pointCloudTreeMetrics().then((rows) => rows.map(mapTreeMetrics3DFromApi)),
    []
  );
  return useListWithMockFallback<TreeMetric3DRow>(fetcher, treeMetrics3D);
}

/**
 * Busca dados reais sem fallback pra mock — usado em Território > Inspeções,
 * onde a ausência de dado deve virar empty state (importar JSON), não mock.
 */
function useApiData<T>(fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Erro ao carregar dados");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

export function useTerritorialInspections() {
  const fetcher = useCallback(
    () => api.territorialInspections().then((rows) => rows.map(mapTerritorialInspectionSummaryFromApi)),
    []
  );
  const { data, loading, error } = useApiData(fetcher);
  return { inspections: data ?? [], loading, error };
}

export function useTerritorialInspectionDetail(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve(null);
    return api.territorialInspectionDetail(inspectionCode).then(mapTerritorialInspectionDetailFromApi);
  }, [inspectionCode]);
  return useApiData(fetcher, [inspectionCode]);
}

export function useTerritorialInspectionObjects(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve([]);
    return api.territorialInspectionObjects(inspectionCode).then((rows) => rows.map(mapDetectedObjectFromApi));
  }, [inspectionCode]);
  const { data, loading, error } = useApiData(fetcher, [inspectionCode]);
  return { objects: data ?? [], loading, error };
}

export function useTerritorialInspectionFieldValidationNotes(inspectionCode: string | null) {
  const fetcher = useCallback(() => {
    if (!inspectionCode) return Promise.resolve([]);
    return api
      .territorialInspectionFieldValidationNotes(inspectionCode)
      .then((rows) => rows.map(mapFieldValidationNoteFromApi));
  }, [inspectionCode]);
  const { data, loading, error } = useApiData(fetcher, [inspectionCode]);
  return { notes: data ?? [], loading, error };
}

export function useTerritorialIndicatorsSummary() {
  const fetcher = useCallback(
    () => api.territorialIndicatorsSummary().then(mapTerritorialIndicatorsSummaryFromApi),
    []
  );
  return useApiData(fetcher);
}

export function useEditorCamadas() {
  const fetcher = useCallback(() => editorApi.camadas().then((rows) => rows.map(mapCamadaGeoFromApi)), []);
  const { data, loading, error } = useApiData(fetcher);
  return { camadas: data ?? [], loading, error };
}

export interface EditorRascunhosFilters {
  status?: string;
  quadraRefId?: number;
  projetoRefId?: number;
  camadaDestino?: string;
}

export function useEditorRascunhos(filters: EditorRascunhosFilters = {}) {
  const [rascunhos, setRascunhos] = useState<RascunhoFeicao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return editorApi
      .rascunhos({
        status: filters.status,
        quadra_ref_id: filters.quadraRefId,
        projeto_ref_id: filters.projetoRefId,
        camada_destino: filters.camadaDestino,
      })
      .then((rows) => setRascunhos(rows.map(mapRascunhoFeicaoFromApi)))
      .catch((err) => setError(err instanceof Error ? err.message : "Erro ao carregar rascunhos"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.quadraRefId, filters.projetoRefId, filters.camadaDestino]);

  useEffect(() => {
    load();
  }, [load]);

  return { rascunhos, loading, error, refetch: load };
}

export function useAreasPiloto() {
  const fetcher = useCallback(() => api.areasPiloto().then((rows) => rows.map(mapAreaPilotoFromApi)), []);
  const { data, loading, error } = useApiData(fetcher);
  return { areasPiloto: data ?? [], loading, error };
}

export function useQuadrasList(areaPilotoId: number | null) {
  const fetcher = useCallback(
    () => api.quadras(areaPilotoId ?? undefined).then((rows) => rows.map(mapQuadraFromApi)),
    [areaPilotoId]
  );
  const { data, loading, error } = useApiData(fetcher, [areaPilotoId]);
  return { quadras: data ?? [], loading, error };
}

export function useInspectionsWithQuadras() {
  const fetcher = useCallback(
    () => api.inspectionsWithQuadras().then((rows) => rows.map(mapInspectionQuadraLinkFromApi)),
    []
  );
  const { data, loading, error } = useApiData(fetcher);
  return { links: data ?? [], loading, error };
}